import { useState, useEffect, useCallback } from "react";
import { useAppAuth } from "@/components/auth/AuthProvider";

import { getDevice, setDevice } from "@/lib/deviceApi";
import { STORAGE_KEYS } from "@/constants";
import type { DeviceType, DeviceInfo } from "@/types";

interface UseDeviceTypeReturn {
  deviceType: DeviceType | null;
  deviceInfo: DeviceInfo | null;
  isLoading: boolean;
  error: string | null;
  setDeviceType: (type: DeviceType, deviceName?: string) => Promise<boolean>;
  clearDevice: () => void;
}

/**
 * Hook to manage device type (baby/parent) with database and localStorage persistence
 */
export function useDeviceType(): UseDeviceTypeReturn {
  const { user, isLoaded } = useAppAuth();

  // Initialize from localStorage immediately to prevent flash on resume
  const [deviceType, setDeviceTypeState] = useState<DeviceType | null>(() => {
    if (typeof window !== "undefined") {
      const cached = localStorage.getItem(STORAGE_KEYS.deviceType);
      return cached as DeviceType | null;
    }
    return null;
  });

  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  // Only show loading if we don't have a cached device type
  const [isLoading, setIsLoading] = useState(() => {
    if (typeof window !== "undefined") {
      return !localStorage.getItem(STORAGE_KEYS.deviceType);
    }
    return true;
  });
  const [error, setError] = useState<string | null>(null);

  // Load device type on mount
  useEffect(() => {
    async function loadDeviceType() {
      if (!isLoaded) return;

      if (!user) {
        setIsLoading(false);
        return;
      }

      // Only show loading if we don't have a cached value
      // This prevents loading flash when returning from background
      const cached = localStorage.getItem(STORAGE_KEYS.deviceType);
      if (!cached) {
        setIsLoading(true);
      }
      setError(null);

      try {
        // Set from cache immediately if available (already done in useState init)
        // Then verify with database in background

        // Then verify with database
        const device = await getDevice();

        if (device) {
          setDeviceInfo(device);
          setDeviceTypeState(device.deviceType);
          // Update localStorage to match DB
          localStorage.setItem(STORAGE_KEYS.deviceType, device.deviceType);
        } else if (cached) {
          // Local storage has value but DB doesn't - clear it
          localStorage.removeItem(STORAGE_KEYS.deviceType);
          setDeviceTypeState(null);
        }
      } catch (err) {
        console.error("[useDeviceType] Error loading device type:", err);
        setError("Failed to load device type");
        // Fallback to localStorage
        const cached = localStorage.getItem(STORAGE_KEYS.deviceType);
        if (cached) {
          setDeviceTypeState(cached as DeviceType);
        }
      } finally {
        setIsLoading(false);
      }
    }

    loadDeviceType();
  }, [user, isLoaded]);

  // Save device type
  const saveDeviceType = useCallback(
    async (type: DeviceType, deviceName?: string): Promise<boolean> => {
      if (!user) {
        setError("Not authenticated");
        return false;
      }

      setIsLoading(true);
      setError(null);

      try {
        const device = await setDevice(type, deviceName);

        if (device) {
          setDeviceInfo(device);
          setDeviceTypeState(device.deviceType);
          localStorage.setItem(STORAGE_KEYS.deviceType, device.deviceType);
          return true;
        } else {
          setError("Failed to save device type");
          return false;
        }
      } catch (err) {
        console.error("[useDeviceType] Error saving device type:", err);
        setError("Failed to save device type");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [user],
  );

  // Clear device type (for settings)
  const clearDevice = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.deviceType);
    setDeviceTypeState(null);
    setDeviceInfo(null);
  }, []);

  return {
    deviceType,
    deviceInfo,
    isLoading,
    error,
    setDeviceType: saveDeviceType,
    clearDevice,
  };
}
